import * as React from 'react';

export type RComponent<P> = React.ComponentClass<P> | React.FC<P>;
export type Element<P> = {
  Component: RComponent<P>;
  props: P;
  container: string;
};
export type Elements = { [key: string]: Element<unknown> };
export type Containers = {
  [key: string]: Elements;
};
export type Listener = () => void;
export type Unsubscribe = () => void;

export class FixedComponentsStore {
  private containers: Containers = {};
  private listeners: Listener[] = [];
  private forceReflowListeners: Listener[] = [];

  public getElements = (container: string): Elements => {
    return this.containers[container] || {};
  };

  public setElement = <P>(container: string, id: string, Component: RComponent<P>, props: P): void => {
    if (!this.containers[container]) {
      this.containers[container] = {};
    }
    this.containers[container][id] = { Component, props, container } as Element<unknown>;
    this.emitChanges();
  };

  public removeElement = (container: string, id: string) => {
    if (this.containers[container] && this.containers[container][id]) {
      delete this.containers[container][id];
      this.emitChanges();
    }
  };

  public subscribe = (listener: Listener): Unsubscribe => {
    this.listeners.push(listener);

    return () => {
      return (this.listeners = this.listeners.filter(l => l !== listener));
    };
  };

  public forceReflow = () => {
    this.forceReflowListeners.forEach(listener => {
      listener();
    });
  };

  public onForceReflow = (listener: Listener): Unsubscribe => {
    this.forceReflowListeners.push(listener);

    return () => {
      return (this.forceReflowListeners = this.forceReflowListeners.filter(l => l !== listener));
    };
  };

  private emitChanges = () => {
    this.listeners.forEach(listener => {
      listener();
    });
  };
}

// Современная замена legacy-контекста (childContextTypes удалён в React 19).
// Провайдер монтируется в App, значение по умолчанию оставлено null,
// чтобы отсутствие провайдера было видно сразу, а не маскировалось общим стором.
export const FixedComponentsContext = React.createContext<FixedComponentsStore | null>(null);
